// The numbers behind the MIS KPI tiles, keyed the way src/config/kpiRegistry.js names
// them. Pure formatting: it takes whatever the /internal endpoints returned and
// builds { key: { value, sub, href? } }. Each endpoint's result is optional (a
// failed call leaves its keys out and its tiles read "Could not load"), and
// nothing is invented: a figure the API reports as "not measured" stays out
// rather than turning into a 0.

const inr = (v) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(v ?? 0);
const num = (v) => String(v ?? 0);
// A null rate means nobody played in the window — not 0%.
const pct = (v) => (v == null ? '—' : `${v}%`);
// Dates on a tile are café-local (IST) whatever timezone the person looking is in: a ticket raised at 11 pm IST
// must not read as the day before to someone browsing from the UK or the US.
const IST = 'Asia/Kolkata';
const istDay = (when) =>
  new Date(when).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', timeZone: IST });
// A bare date such as "2026-09-30" is a café-local calendar day, so it starts at midnight IST — not midnight UTC,
// which is 05:30 IST and, formatted in a western timezone, the evening before.
const istMidnight = (isoDate) => new Date(`${isoDate}T00:00:00+05:30`);

// "8–9 pm", "11 am–12 pm", "11 pm–12 am", "12–1 am" for a café-local hour of the day (0-23).
export function hourLabel(h) {
  const clock = (x) => x % 12 || 12;
  const half = (x) => (x % 24 < 12 ? 'am' : 'pm');
  const next = (h + 1) % 24;
  return half(h) === half(next)
    ? `${clock(h)}–${clock(next)} ${half(next)}`
    : `${clock(h)} ${half(h)}–${clock(next)} ${half(next)}`;
}

export function buildKpiValues({
  dash, players, sessions, duration, hours, repeat, mix, recharge, fnb, nolag, cashOnline, pcFnb, byHour, perScreen,
  perSession, tickets, payments, uptime, weekly, usage, health, reinstalls, activation, byCafe, mom, topGames, adoption, dashUsage,
  supportByCafe,
}) {
  const out = {};

  // Café, screen and revenue tiles come from the dashboard summary; without it they
  // stay unfilled ("Could not load") rather than turning into zeros.
  if (dash) {
    const estimated = dash.cafes_join_date_estimated ?? 0;
    out.cafes_total = { value: num(dash.total_cafes), sub: `${num(dash.active_cafes)} active`, href: '/cafes' };
    out.cafes_active = {
      value: num(dash.active_cafes),
      sub: `activity in the last ${dash.active_cafe_window_days ?? 7} days`,
      href: '/cafes',
    };
    out.cafes_new_today = {
      value: num(dash.new_cafes_today),
      sub: `${num(dash.new_cafes_7d)} in 7 days · ${num(dash.new_cafes_30d)} in 30` +
        (estimated > 0 ? ` · ${estimated} older without an exact date` : ''),
      href: '/cafes',
    };
    out.cafes_new_7d = {
      value: num(dash.new_cafes_7d),
      sub: `${num(dash.new_cafes_today)} today · ${num(dash.new_cafes_30d)} in 30 days`,
      href: '/cafes',
    };
    out.cafes_inactive_24h = {
      value: num(dash.cafes_inactive_24h),
      sub: `${num(dash.cafes_quiet_24h)} went quiet · ${num(dash.cafes_never_active)} never started`,
      href: '/cafes',
    };
    out.screens_total = {
      value: num(dash.total_pcs),
      sub: `${num(dash.online_pcs)} online · ${num(dash.active_screens)} in session`,
      href: '/pcs',
    };
    out.screens_offline = { value: num(dash.offline_pcs), sub: `of ${num(dash.total_pcs)} screens`, href: '/pcs' };
    out.revenue_mtd = { value: inr(dash.monthly_revenue), sub: 'month to date', href: '/analytics' };
    // Unit economics: this month's revenue spread over cafés and screens. With no
    // café / screen to divide by there is no average to show — "—", not ₹0.
    const perUnit = (units) => (units > 0 ? inr(dash.monthly_revenue / units) : '—');
    out.revenue_per_cafe = {
      value: perUnit(dash.active_cafes),
      sub: `month to date, per active café (${num(dash.active_cafes)}) · ${perUnit(dash.total_cafes)} per café onboarded (${num(dash.total_cafes)})`,
      href: '/analytics',
    };
    out.revenue_per_screen = {
      value: perUnit(dash.total_pcs),
      sub: `month to date, across ${num(dash.total_pcs)} screens · ${perUnit(dash.online_pcs)} per online screen (${num(dash.online_pcs)})`,
      href: '/pcs',
    };
  }

  if (players) {
    out.players_active_today = {
      value: num(players.active_today),
      sub: `${num(players.active_7d)} in 7 days · ${num(players.active_30d)} in 30`,
    };
    out.players_active_7d = {
      value: num(players.active_7d),
      sub: `${num(players.active_today)} today · ${num(players.active_30d)} in 30 days`,
    };
    out.dau_wau = {
      value: `${num(players.active_today)} / ${num(players.active_7d)}`,
      sub: 'today / last 7 days',
    };
    if (players.new_today != null) {
      out.players_new_today = {
        value: num(players.new_today),
        sub: `${num(players.new_7d)} in 7 days · ${num(players.new_30d)} in 30`,
      };
      out.players_new_7d = {
        value: num(players.new_7d),
        sub: `${num(players.new_today)} today · ${num(players.new_30d)} in 30 days`,
      };
    }
  }
  if (sessions) {
    out.sessions_today = {
      value: num(sessions.sessions_today),
      sub: `average ${num(sessions.average_per_day)} a day`,
    };
    const last7 = (sessions.days || []).slice(-7).reduce((sum, d) => sum + (d.sessions || 0), 0);
    out.sessions_7d = { value: num(last7), sub: 'last 7 days, today included' };
  }
  if (duration) {
    const avg7 = duration.avg_7d_minutes == null ? '—' : `${duration.avg_7d_minutes} min`;
    out.session_duration_today = {
      value: duration.avg_today_minutes == null ? '—' : `${duration.avg_today_minutes} min`,
      sub: `7-day average ${avg7} (${num(duration.sessions_7d)} sessions)`,
    };
  }
  if (hours) {
    out.gaming_hours_today = {
      value: `${num(hours.hours_today)} h`,
      sub: `${num(hours.hours_7d)} h in 7 days · ${num(hours.hours_30d)} h in 30`,
    };
    out.gaming_hours_7d = {
      value: `${num(hours.hours_7d)} h`,
      sub: `${num(hours.hours_30d)} h in 30 days`,
    };
  }
  if (repeat) {
    out.players_repeat = {
      value: num(repeat.repeat_7d),
      sub: `${pct(repeat.repeat_rate_7d)} of ${num(repeat.players_7d)} ${repeat.players_7d === 1 ? 'player' : 'players'} this week · 30 days: ${num(repeat.repeat_30d)}`,
    };
  }
  if (mix) {
    out.players_new_vs_returning = {
      value: `${num(mix.new_7d)} / ${num(mix.returning_7d)}`,
      sub: 'new / returning, last 7 days',
    };
  }
  // F&B revenue is shop-item money that ARRIVED (cash / online). A shop sale paid
  // from a wallet balance is not in it — that money was counted at top-up — so the
  // value sold, wallet-funded included, is shown beside it. Refunds are not netted:
  // they can't be attributed to F&B alone. null = not measured: leave it unfilled.
  if (fnb && fnb.today != null) {
    out.fnb_today = {
      value: inr(fnb.today.revenue),
      sub: `${inr(fnb.today.sold)} sold incl. wallet · week ${inr(fnb.this_week?.revenue)} · month ${inr(fnb.this_month?.revenue)}`,
    };
  }
  // Support tickets. "Open" = open + in progress (unresolved right now). The closure rate is a share
  // of RECENTLY RAISED tickets that are resolved or closed now — a cohort share, not a speed: the
  // ticket table records no closing time, recent tickets have had little time to close, and a
  // deleted ticket vanishes from the count (so deleting open ones would flatter it). Said on the
  // tile. With no tickets raised in the window there is nothing to measure: a dash, never 0%.
  if (tickets) {
    const day = istDay;
    const rate = (v) => (v == null ? '—' : `${v}%`);
    const bits = [`${tickets.open_by_status?.open ?? 0} open`, `${tickets.open_by_status?.in_progress ?? 0} in progress`];
    if (tickets.urgent_or_high_open > 0) bits.push(`${tickets.urgent_or_high_open} urgent or high priority`);
    if (tickets.oldest_open_at) bits.push(`oldest raised ${day(tickets.oldest_open_at)}`);
    bits.push(`${tickets.total} ${tickets.total === 1 ? 'ticket' : 'tickets'} in all`);
    out.tickets_open = { value: String(tickets.open), sub: bits.join(' · '), href: '/support' };
    out.ticket_closure_rate = {
      value: rate(tickets.closure_rate_30d),
      sub: tickets.created_30d > 0
        ? `${tickets.closed_of_created_30d} of ${tickets.created_30d} tickets raised in the last 30 days are resolved or closed` +
          ` · last 7 days: ${tickets.closed_of_created_7d} of ${tickets.created_7d}` +
          (tickets.closure_rate_7d != null ? ` (${tickets.closure_rate_7d}%)` : '') +
          ' · a share of recent tickets, not a speed: closing times are not recorded, and deleted tickets drop out of the count'
        : 'no tickets raised in the last 30 days, so there is nothing to measure',
      href: '/support',
    };
  }
  // Re-installations — PCs whose noLag (or Windows) was reinstalled in the last 7 days. It counts PCs,
  // each once: only a PC's LATEST reinstall time is stored, so one reinstalled twice in a week counts once
  // (a floor on events); the all-time total adds up every PC's own counter and does count every event.
  // Reinstalls have only been recorded since a set date, so the 30-day figure is partial until that has
  // passed — said on the tile, with the date. A reinstall is a reported uninstall or a manual setup, not a
  // diskless PC restarting. A café that could not be read is said, not skipped; none readable = not
  // measured. A measured 0 is a real 0.
  if (reinstalls) {
    const unmeasured = reinstalls.cafes_unmeasured ?? 0;
    const left = unmeasured > 0
      ? ` · ${unmeasured} ${unmeasured === 1 ? 'café' : 'cafés'} could not be read and ${unmeasured === 1 ? 'is' : 'are'} left out`
      : '';
    if (reinstalls.last_7d == null) {
      out.reinstalls_week = { value: '—', sub: 'not measured: no café database could be read' };
    } else {
      const day = istDay;
      const since = istMidnight(reinstalls.tracking_since);
      const known = !Number.isNaN(since.getTime());
      const partialUntil = new Date(since.getTime() + 30 * 86400000);
      const partial = known && Date.now() < partialUntil.getTime();
      const sinceText = known
        ? ` · recorded only since ${day(since)}${partial ? `, so the 30-day figure is partial until ${day(partialUntil)}` : ''}`
        : '';
      const pcs = (n) => `${n} ${n === 1 ? 'PC' : 'PCs'}`;
      out.reinstalls_week = {
        value: num(reinstalls.last_7d),
        sub: `${reinstalls.last_7d === 1 ? 'PC' : 'PCs'} reinstalled in the last 7 days · 30 days: ${num(reinstalls.last_30d)}` +
          ` · ${num(reinstalls.recorded_total)} ${reinstalls.recorded_total === 1 ? 'reinstall' : 'reinstalls'} recorded in all` +
          ` · ${pcs(reinstalls.pcs_away)} uninstalled and not back yet` +
          ' · counts each PC once: only its latest reinstall is stored' +
          `${sinceText}` +
          ' · a reinstall is a reported uninstall or a manual setup, not a diskless PC restarting' +
          left,
      };
    }
  }
  // Post-install activation — of the PCs installed in the last 7 days, how many have had a CUSTOMER session
  // (a customer account: a staff or owner test login does not count). A share of recent installs, not a
  // speed: a PC installed yesterday may simply not have been used yet, so a short window reads low. Only
  // PCs with an exact install date are counted: older ones carry an estimated date (which would make them
  // look used the moment they were "installed") or none, and are left out and counted. A reinstall is not
  // a new install. Nothing installed = nothing to measure: a dash, never 0%. A café that could not be read
  // is said; none readable = not measured.
  if (activation) {
    const unmeasured = activation.cafes_unmeasured ?? 0;
    const left = unmeasured > 0
      ? ` · ${unmeasured} ${unmeasured === 1 ? 'café' : 'cafés'} could not be read and ${unmeasured === 1 ? 'is' : 'are'} left out`
      : '';
    const w7 = activation.last_7d;
    const w30 = activation.last_30d;
    if (w7 == null || w30 == null) {
      out.post_install_activation_week = { value: '—', sub: 'not measured: no café database could be read' };
    } else {
      const pcs = (n) => `${n} ${n === 1 ? 'PC' : 'PCs'}`;
      const share = (w) => (w.share == null ? '—' : `${w.share}%`);
      const day = istDay;
      const since = istMidnight(activation.tracking_since);
      const known = !Number.isNaN(since.getTime());
      const partialUntil = new Date(since.getTime() + 30 * 86400000);
      const partial = known && Date.now() < partialUntil.getTime();
      const lead = w7.installed === 0
        ? 'no PC was installed in the last 7 days, so there is nothing to measure'
        : `${w7.activated} of ${pcs(w7.installed)} installed in the last 7 days ${w7.activated === 1 ? 'has' : 'have'} had a customer session`;
      const month = w30.installed === 0
        ? ' · 30 days: no PC installed'
        : ` · 30 days: ${w30.activated} of ${w30.installed} (${share(w30)})`;
      const undated = activation.pcs_undated > 0
        ? ` · ${pcs(activation.pcs_undated)} with no exact install date left out`
        : '';
      const sinceText = known
        ? ` · exact install dates only exist since ${day(since)}${partial ? `, so the 30-day figure is partial until ${day(partialUntil)}` : ''}`
        : '';
      out.post_install_activation_week = {
        value: share(w7),
        sub: `${lead}${month}${undated}` +
          ' · a share of recent installs, not a speed: a PC installed yesterday may not have been used yet' +
          ' · a customer session means a customer account, not staff or the owner' +
          ` · a reinstall is not a new install${sinceText}${left}`,
      };
    }
  }
  // Revenue by café — money in, café by café. DAILY "Revenue per café": today's money in across every café,
  // spread over the ACTIVE cafés (the same divisor as the Monthly "Revenue per café": activity in the last 7
  // days), with the cafés that brought in the most. MONTHLY "Revenue by café": this month so far, with the
  // biggest cafés and each one's share. Revenue is money in, so a wallet balance that is spent is not counted
  // again (it was counted at top-up, maybe in another café or on another day): a café that mostly serves
  // wallet-funded customers can rank low — said on the tile. The daily figure is an average, and cafés differ
  // widely in size. With no active café to divide by it is a dash, never ₹0; a measured ₹0 is real. A café that
  // could not be read is said; none readable = not measured.
  if (byCafe) {
    const unreachable = byCafe.cafes_unreachable ?? 0;
    const cafes = (n) => `${n} ${n === 1 ? 'café' : 'cafés'}`;
    const left = unreachable > 0
      ? ` · ${cafes(unreachable)} could not be read and ${unreachable === 1 ? 'is' : 'are'} left out`
      : '';
    const wallet = ' · money in, so a wallet balance spent is not counted again';

    const day = byCafe.today;
    if (day == null) {
      out.revenue_per_cafe_today = { value: '—', sub: 'not measured: no café database could be read', href: '/cafes' };
    } else {
      const active = dash?.active_cafes;
      const onboarded = dash?.total_cafes;
      const per = (n) => (n > 0 ? inr(day.revenue / n) : '—');
      const spread = dash
        ? ` ÷ ${num(active)} active ${active === 1 ? 'café' : 'cafés'} (activity in the last ${dash.active_cafe_window_days ?? 7} days)` +
          ` · ${per(onboarded)} per café onboarded (${num(onboarded)})`
        : ' · the active-café count did not load, so there is no average';
      const top = day.top.length > 0
        ? ` · most from ${day.top.map((c) => `${c.name} ${inr(c.revenue)}`).join(', ')}`
        : ' · no café brought in money today';
      out.revenue_per_cafe_today = {
        value: dash ? per(active) : '—',
        sub: `${inr(day.revenue)} money in today${spread}` +
          ` · ${day.cafes_paid} of ${cafes(day.cafes_read)} took money${top}${wallet}` +
          ` · an average: cafés differ widely in size${left}`,
        href: '/cafes',
      };
    }

    const month = byCafe.this_month;
    if (month == null) {
      out.revenue_by_cafe_month = { value: '—', sub: 'not measured: no café database could be read', href: '/analytics' };
    } else {
      // A share only means something against a positive total.
      const share = (revenue) => (month.revenue > 0 ? ` (${Math.round((1000 * revenue) / month.revenue) / 10}%)` : '');
      const top = month.top.length > 0
        ? `most from ${month.top.map((c) => `${c.name} ${inr(c.revenue)}${share(c.revenue)}`).join(', ')}`
        : 'no café brought in money this month';
      out.revenue_by_cafe_month = {
        value: inr(month.revenue),
        sub: `money in this month so far · ${month.cafes_paid} of ${cafes(month.cafes_read)} took money · ${top}` +
          `${wallet} · every café month by month is on the Analytics page${left}`,
        href: '/analytics',
      };
    }
  }
  // Month-on-month revenue — this month so far against the SAME elapsed days of last month, never a
  // part-finished month against a finished one (that would always read as a collapse early in the month), with
  // last month in full beside it. Money in. It adds up every café, including any that joined since last month,
  // so growth from new cafés is in the figure — it is not a same-café comparison. A last-month base of ₹0 or
  // less has no percentage: a dash, never 0% and never infinite. Early in the month the base is a few days of
  // trading and the percentage swings widely, so the tile says how many days it rests on. null = not measured.
  if (mom) {
    const unreachable = mom.cafes_unreachable ?? 0;
    const left = unreachable > 0
      ? ` · ${unreachable} ${unreachable === 1 ? 'café' : 'cafés'} could not be read and ${unreachable === 1 ? 'is' : 'are'} left out`
      : '';
    if (mom.this_month == null) {
      out.revenue_mom_month = { value: '—', sub: 'not measured: no café database could be read', href: '/analytics' };
    } else {
      const days = (n) => `${n} ${n === 1 ? 'day' : 'days'}`;
      const change = mom.change_percent;
      const lead = change == null
        ? `no % change to show: the same ${days(mom.days_compared)} of last month had no money in (${inr(mom.last_month_same_days)}), against ${inr(mom.this_month)} so far this month`
        : `${inr(mom.this_month)} so far this month (${days(mom.days_elapsed)}) vs ${inr(mom.last_month_same_days)} over the same ${days(mom.days_compared)} of last month`;
      const shorter = mom.days_compared < mom.days_elapsed - 0.05
        ? ' · last month was shorter, so all of it is the comparison'
        : '';
      const early = mom.days_elapsed < 3
        ? ` · only ${days(mom.days_elapsed)} into the month, so this swings widely`
        : '';
      out.revenue_mom_month = {
        value: change == null ? '—' : `${change > 0 ? '+' : ''}${change}%`,
        sub: `${lead}${shorter}${early}` +
          ` · last month in full: ${inr(mom.last_month)} (${days(mom.last_month_days)})` +
          ' · counts every café, including any that joined since last month — not a same-café comparison' +
          `${' · money in, so a wallet balance spent is not counted again'}${left}`,
        href: '/analytics',
      };
    }
  }
  // Most-used games — the games that took the most play time across every café in the last 7 days: the
  // Activity page's Games table, ranked by time (then launches). Play time is the title's FOCUS time as
  // measured by the kiosk where it reported, otherwise an ESTIMATE from the gaps between launches, so the tile
  // says what share was measured and marks estimated titles "est." instead of presenting a guess as a fact.
  // Ranked by time spent, not revenue; apps are a separate table; a café whose kiosks do not report launches
  // adds nothing, which reads the same as nobody playing — all said on the tile. The headline is the top game's
  // name, shortened (the big number has no room for a long title; the full name is below). Nothing played is a
  // dash; a café that could not be read is said, and none readable = not measured.
  if (topGames) {
    const unreachable = topGames.cafes_unreachable ?? 0;
    const left = unreachable > 0
      ? ` · ${unreachable} ${unreachable === 1 ? 'café' : 'cafés'} could not be read and ${unreachable === 1 ? 'is' : 'are'} left out`
      : '';
    if (topGames.titles_played == null) {
      out.most_used_games_week = { value: '—', sub: 'not measured: no café database could be read', href: '/admin-activity' };
    } else if (topGames.top.length === 0) {
      out.most_used_games_week = {
        value: '—',
        sub: `no game was launched in the last ${topGames.days} days · a café whose kiosks do not report launches adds nothing, which looks the same as nobody playing${left}`,
        href: '/admin-activity',
      };
    } else {
      const time = (m) => (m < 90 ? `${m} min` : `${Math.round(m / 6) / 10} h`);
      const short = (name) => (name.length > 18 ? `${name.slice(0, 17)}…` : name);
      const players = (n) => `${n} ${n === 1 ? 'player' : 'players'}`;
      const list = topGames.top
        .map((g, i) => `${i + 1}. ${g.game} ${time(g.minutes)}${g.measured ? '' : ' (est.)'}, ${players(g.players)}`)
        .join(' · ');
      const measured = topGames.total_minutes > 0
        ? ` · ${Math.round((topGames.measured_ratio ?? 0) * 100)}% of the play time was measured by the kiosk, the rest (est.) is estimated from the gaps between launches`
        : '';
      out.most_used_games_week = {
        value: short(topGames.top[0].game),
        sub: `by time played, last ${topGames.days} days: ${list}` +
          ` · ${topGames.titles_played} ${topGames.titles_played === 1 ? 'game' : 'games'} played, ${time(topGames.total_minutes)} in all${measured}` +
          ' · ranked by time in the foreground, not revenue · apps are counted separately' +
          ` · a café whose kiosks do not report launches adds nothing${left}`,
        href: '/admin-activity',
      };
    }
  }
  // Feature adoption — the share of cafés that used each product feature at least once in the last 7 days
  // (the Feature Usage Heatmap's own definition), over the cafés that could actually be READ: a café that
  // cannot be read is left out and said, never counted as one that "did not adopt". The headline is the plain
  // average of the features' rates, which blends unlike features, so every feature's own rate is listed beside
  // it. It counts cafés, not how much they use a feature (one launch is adoption), and it includes cafés that
  // were closed or idle this week. News is announcements a café published, not a customer action — marked.
  // Nothing readable = not measured; a measured 0% is real.
  if (adoption) {
    const unmeasured = adoption.cafes_unmeasured ?? 0;
    const cafes = (n) => `${n} ${n === 1 ? 'café' : 'cafés'}`;
    const left = unmeasured > 0
      ? ` · ${cafes(unmeasured)} could not be read and ${unmeasured === 1 ? 'is' : 'are'} left out (not counted as non-adopters)`
      : '';
    if (adoption.average_percent == null) {
      out.feature_adoption_week = {
        value: '—',
        sub: unmeasured > 0
          ? 'not measured: no café database could be read'
          : 'no café has a database yet, so there is nothing to measure',
        href: '/admin-activity',
      };
    } else {
      const list = adoption.features
        .map((f) => `${f.label}${f.customer_action ? '' : ' (published by the café)'} ${f.adoption_percent}%`)
        .join(' · ');
      out.feature_adoption_week = {
        value: `${adoption.average_percent}%`,
        sub: `average of ${adoption.features.length} features, each the share of the ${cafes(adoption.cafes_read)} read that used it at least once in the last ${adoption.days} days: ${list}` +
          ` · ${adoption.features_used} of ${adoption.features.length} features used by at least one café` +
          ' · counts cafés, not how much they use a feature: one launch is adoption' +
          ` · includes cafés that were closed or idle this week${left}`,
        href: '/admin-activity',
      };
    }
  }
  // Dashboard usage — how much the café ADMIN DASHBOARD was used in the last 7 days: page views (arriving on a
  // page — time on page and clicks are not views), by café users only. Our own platform staff opening a café's
  // dashboard to support it is not café usage, so those views are left out and said, which is why this can be
  // lower than the Activity page's total. It comes from a browser tracker, so a page opened for under ~15 seconds
  // before the tab closed can be missing, and a café where nobody opened the dashboard looks the same as one whose
  // tracker is not sending — both said on the tile. The table not readable = not measured; zero views is a real 0.
  if (dashUsage) {
    if (dashUsage.page_views == null) {
      out.dashboard_usage_week = { value: '—', sub: 'not measured: the dashboard activity log could not be read', href: '/admin-activity' };
    } else {
      const many = (n, one, other) => `${n.toLocaleString('en-IN')} ${n === 1 ? one : other}`;
      const staff = dashUsage.platform_staff_views > 0
        ? ` · ${many(dashUsage.platform_staff_views, 'view', 'views')} by our own staff are left out (café users only)`
        : '';
      const tracker = ' · a café where nobody opened the dashboard looks the same as one whose tracker is not sending';
      if (dashUsage.page_views === 0) {
        out.dashboard_usage_week = {
          value: '0',
          sub: `no café user opened the admin dashboard in the last ${dashUsage.days} days${staff}${tracker}`,
          href: '/admin-activity',
        };
      } else {
        const of = dash?.total_cafes > 0 ? ` (of ${num(dash.total_cafes)} onboarded)` : '';
        const pages = dashUsage.top_pages.length > 0
          ? ` · most viewed: ${dashUsage.top_pages.map((p) => `${p.page} ${p.views.toLocaleString('en-IN')}`).join(', ')}`
          : '';
        out.dashboard_usage_week = {
          value: dashUsage.page_views.toLocaleString('en-IN'),
          sub: `page views in the last ${dashUsage.days} days, by ${many(dashUsage.people, 'person', 'people')} in ${many(dashUsage.cafes, 'café', 'cafés')}${of}` +
            ` across ${many(dashUsage.sessions, 'browser session', 'browser sessions')}${pages}${staff}` +
            ' · a view is arriving on a page: time on page and clicks are not counted' +
            ` · a page opened for under 15 seconds before the tab closed can be missing${tracker}`,
          href: '/admin-activity',
        };
      }
    }
  }
  // Support tickets (customer success) — which cafés are raising tickets: tickets raised in the last 7 days and
  // the cafés raising the most, with how many of them are still open (open or in progress). The customer lens on the
  // same queue Café performance shows as a backlog: who needs attention. It counts what a café RAISED, not how much
  // trouble it has — a café that never uses the support form raises none, a bigger café raises more (nothing is divided
  // by size), and a deleted ticket is gone from the count. LIVE cafés only: an archived café is out of every metric,
  // so its tickets are not counted and it is not in the café total. A week with none raised is a true 0.
  if (supportByCafe) {
    const w = supportByCafe.last_7d;
    const m = supportByCafe.last_30d;
    const cafes = (n) => `${n} ${n === 1 ? 'café' : 'cafés'}`;
    const tickets = (n) => `${n} ${n === 1 ? 'ticket' : 'tickets'}`;
    const lead = w.raised === 0
      ? 'none raised in the last 7 days'
      : `raised in the last 7 days by ${w.cafes} of ${cafes(supportByCafe.cafes_total)}`;
    const most = w.top.length > 0 ? ` · most from ${w.top.map((c) => `${c.name} (${c.raised})`).join(', ')}` : '';
    const open = w.raised > 0 ? ` · ${w.open} of them still open or in progress` : '';
    out.support_tickets_by_cafe_week = {
      value: num(w.raised),
      sub: `${lead}${most}${open} · 30 days: ${tickets(m.raised)} from ${cafes(m.cafes)}` +
        ' · counts what a café raised, not how much trouble it has: a café that never uses the support form raises none,' +
        ' bigger cafés raise more, and a deleted ticket drops out of the count' +
        ' · tickets from archived cafés are not counted',
      href: '/support',
    };
  }
  // System uptime — the share of the server's own 5-minute health checks that came back healthy,
  // read from the same log as the System Health page, so its 30-day figure is the same number.
  // Two things a bare percentage would hide are said on the tile: COVERAGE (time with no check is
  // neither up nor down, so a sweep that ran for two days of the month must not read like a month)
  // and the last check's age and verdict (a check that has stopped, or an unhealthy one right now).
  // It is not availability as a customer feels it: the check runs on the server it is judging, so a
  // full outage leaves a gap, and "healthy" is only the conditions the sweep tests — both said on
  // the tile. A window in which no check ran has no figure: a dash, never 0%; a MEASURED 0% (every
  // check unhealthy) is shown as 0%. "Not measured" says so instead of "Could not load".
  if (uptime) {
    const href = '/system-health';
    const ago = (m) => {
      if (m == null) return '—';
      if (m < 1) return 'under a minute ago';
      if (m < 90) return `${Math.round(m)} min ago`;
      if (m < 2880) return `${Math.round(m / 6) / 10} h ago`;
      return `${Math.round(m / 1440)} days ago`;
    };
    const definition =
      ' · "healthy" = the server\'s own 5-minute check found no 5xx rate of 5% or more, database pool or disk' +
      ' 90% full, half the PCs silent for 24 h, or no background workers' +
      ' · it runs on the server itself, so a full outage leaves a gap, not downtime: counting that needs a check from outside';
    const make = (w, period, extra = '') => {
      if (uptime.log_state === 'unreadable') {
        return { value: '—', sub: 'not measured: the server could not read its health-check log', href };
      }
      if (uptime.log_state === 'empty') {
        return { value: '—', sub: 'not measured yet: no health check has been recorded', href };
      }
      if (!w) return null;
      const stopped = uptime.last_check_age_minutes > 3 * (uptime.interval_minutes ?? 5);
      const body = w.uptime_percent == null
        ? `no health check ran ${period}`
        : `${w.healthy_checks} of ${w.checks} checks healthy ${period}`;
      const coverage = `the check ran for ${w.coverage_percent}% of that time` +
        (w.coverage_percent < 100 ? ' — the rest is a gap, counted as neither up nor down' : '');
      const last = `last check ${ago(uptime.last_check_age_minutes)}` +
        (stopped ? ' — the check has stopped, so this figure is going stale' : '') +
        (uptime.healthy_now === false ? ' · the latest check was UNHEALTHY' : '');
      return {
        value: w.uptime_percent == null ? '—' : `${w.uptime_percent}%`,
        sub: `${body} · ${coverage} · ${last}${extra}${definition}`,
        href,
      };
    };
    const month = uptime.last_30d;
    const monthText = month
      ? ` · 30 days: ${month.uptime_percent == null ? '—' : `${month.uptime_percent}%`} (the check ran for ${month.coverage_percent}% of them)`
      : '';
    const today = make(uptime.today, 'so far today');
    const week = make(uptime.last_7d, 'in the last 7 days', monthText);
    if (today) out.uptime_today = today;
    if (week) out.uptime_7d = week;
  }
  // Payment issues — failed payments in the last 7 days, counted per distinct Razorpay order from
  // what Razorpay's webhook and the kiosk REPORTED. It is a floor, and the tile says so: a customer
  // who closes the payment panel or loses the connection leaves no row, and a failed attempt the
  // customer then retried and paid is still counted (a shop purchase's success row does not carry
  // the order id, so the two cannot be matched). A failure only the kiosk saw has no amount on
  // record, so the rupee figure counts the others and says how many it leaves out — never ₹0.
  // Kiosk timeouts (10 minutes, nothing paid) are a different event: shown beside it, not added in.
  // null = the audit table could not be read: a dash that says so, never 0. No link: the Audit
  // Logs page cannot be opened already filtered to these rows.
  if (payments && payments.last_7d != null) {
    const w = payments.last_7d;
    const n = (v) => (v == null ? '—' : String(v));
    const top = payments.top_reasons_30d?.[0];
    let amount = '';
    if (w.failed > 0) {
      amount = w.amount_unknown >= w.failed
        ? ' · amount not recorded'
        : ` · ${inr(w.amount_attempted)} attempted` +
          (w.amount_unknown > 0 ? ` (+${w.amount_unknown} with no amount recorded)` : '');
    }
    out.payment_issues = {
      value: num(w.failed),
      sub: `${w.failed === 0 ? 'none reported in the last 7 days' : 'failed in the last 7 days'}` +
        ` · today ${n(payments.today?.failed)} · 30 days ${n(payments.last_30d?.failed)}${amount}` +
        (w.timed_out > 0 ? ` · ${w.timed_out} timed out at the kiosk (10 min, nothing paid)` : '') +
        (top ? ` · top reason, 30 days: ${top.reason} (${top.count})` : '') +
        ' · only what Razorpay or the kiosk reported: a closed payment panel or a lost connection leaves no record,' +
        ' and a failed attempt the customer then retried and paid is still counted' +
        ' · search "payment_failed" in Audit Logs for the rows',
    };
  } else if (payments) {
    // The server answered but could not read the audit log: say so, rather than "Could not load".
    out.payment_issues = { value: '—', sub: 'not measured: the server could not read the audit log' };
  }
  // Screen utilization — gaming hours as a share of (screens x EVERY hour of the period), over the
  // last 7 days. The denominator is blunt on purpose: the system records neither how long a PC was
  // switched on nor opening hours, so closed hours count as unused and the figure reads LOW — said
  // on the tile, never hidden behind an invented opening-hours assumption. Not clamped: over 100%
  // would mean overlapping recorded sessions, a data problem worth seeing. No screens = nothing to
  // measure: a dash, not 0%. Only built when the backend sends it (an older backend does not).
  if (hours && 'utilization_7d' in hours) {
    const pct1 = (v) => (v == null ? '—' : v > 0 && v < 0.1 ? '<0.1%' : `${Math.round(v * 10) / 10}%`);
    const screens = `${hours.screens} ${hours.screens === 1 ? 'screen' : 'screens'}`;
    out.screen_utilization_week = {
      value: pct1(hours.utilization_7d),
      sub: hours.screens > 0
        ? `${num(hours.hours_7d)} h played of ${num(hours.available_hours_7d)} h available (${screens} × 7 days × 24 h)` +
          ` · today ${pct1(hours.utilization_today)} · 30 days ${pct1(hours.utilization_30d)}` +
          ' · closed hours count too — opening hours and PC on-time are not recorded, so it reads low'
        : 'no screens seen in the last 90 days, so there is nothing to measure',
    };
  }
  // Revenue per session — money in divided by the sessions that STARTED in the period. An
  // AVERAGE, and labelled as one: money cannot be tied to a session (a top-up happens before
  // any session and funds many later), so a day of top-ups with few sessions reads high. With
  // no session started there is nothing to average: a dash, never ₹0 — and if money DID come in
  // that day, it says so instead of hiding it. A true ₹0 only shows when sessions ran and no
  // money came in (revenue is always measurable).
  if (perSession && perSession.today != null) {
    const t = perSession.today;
    const per = (w) => (w && w.per_session != null ? inr(w.per_session) : '—');
    const basis = t.sessions > 0
      ? `${inr(t.revenue)} money in ÷ ${t.sessions} ${t.sessions === 1 ? 'session' : 'sessions'} started today`
      : (t.revenue !== 0
        ? `${inr(t.revenue)} money in but no session started today, so nothing to average`
        : 'no session started today, so nothing to average');
    out.rev_per_session_today = {
      value: t.per_session != null ? `${inr(t.per_session)} / session` : '—',
      sub: `${basis} · week ${per(perSession.this_week)} · month ${per(perSession.this_month)} per session` +
        ' · an average, since money can\'t be tied to one session',
    };
  }
  // Revenue per screen — money in divided by the screens (PCs seen in the last 90 days).
  // An AVERAGE, and labelled as one: money cannot be tied to a PC (a top-up funds whatever is
  // played, anywhere). The screen count is today's, so a fleet that grew during the week or
  // month makes those figures read slightly low. No screens = nothing to average: a dash,
  // never ₹0. The same endpoint feeds the Daily, Weekly and Monthly entries of the list.
  if (perScreen) {
    const days = perScreen.active_window_days ?? 90;
    const make = (w, extra = '') => {
      if (!w) return null;
      const screens = `${w.screens} ${w.screens === 1 ? 'screen' : 'screens'}`;
      return {
        value: w.per_screen != null ? `${inr(w.per_screen)} / screen` : '—',
        sub: w.screens > 0
          ? `${inr(w.revenue)} money in ÷ ${screens} seen in the last ${days} days · an average, since money can't be tied to one PC${extra}`
          : `no screens seen in the last ${days} days, so there is nothing to average`,
      };
    };
    const today = make(perScreen.today);
    const week = make(perScreen.this_week);
    const month = make(perScreen.this_month, " · divided by today's screen count");
    if (today) out.rev_per_screen_today = today;
    if (week) out.rev_per_screen_week = week;
    if (month) out.rev_per_screen_month = month;
  }
  // Revenue per hour — money in per elapsed hour of this week, and when it comes in.
  // It is money counted when it ARRIVED (a top-up lands in the hour it was put in, not when
  // it is played), over all 24 hours (opening hours are not known, so a café that is shut
  // overnight pulls the average down) — both said on the tile. A week with no money in is a
  // true ₹0 an hour (revenue is always measurable); only "not measured" leaves it unfilled.
  if (byHour && byHour.this_week != null) {
    const w = byHour.this_week;
    const mo = byHour.this_month;
    const busiest = w.peak_hour != null
      ? ` · busiest ${hourLabel(w.peak_hour)}${w.peak_share != null ? ` (${w.peak_share}% of it)` : ''}`
      : '';
    const month = mo && mo.average_per_hour != null ? ` · this month ${inr(mo.average_per_hour)} / hour` : '';
    out.revenue_per_hour_week = {
      value: w.average_per_hour != null ? `${inr(w.average_per_hour)} / hour` : '—',
      sub: `${inr(w.total)} money in over ${w.elapsed_hours} h this week${busiest}${month}` +
        ' · counted when it arrived, over all 24 hours',
    };
  }
  // PC vs F&B — a monthly KPI, so it reads this month. Two different questions, kept apart:
  //  * SOLD: PC packs vs shop items whatever funded them (wallet-funded included). This is
  //    the real PC-vs-F&B comparison and the headline — but it is NOT revenue, and it only
  //    exists from the revenue-model start date (before that: not available; straddling it:
  //    partial), so those cases are said on the tile, never filled with a made-up number.
  //  * PAID IN: revenue. PC packs and shop orders paid for directly are attributable; wallet
  //    top-ups fund both, so they are shown as their own figure and not split.
  // The two shares are rounded so they always add up to 100.
  if (pcFnb && pcFnb.this_month != null) {
    const { sold, money_in: paidIn } = pcFnb.this_month;
    const soldTotal = sold ? sold.pc + sold.fnb : 0;
    const pcShare = soldTotal > 0 ? Math.round((100 * sold.pc) / soldTotal) : null;
    const soldText = sold
      ? `sold ${inr(sold.pc)} PC · ${inr(sold.fnb)} F&B (wallet-funded included, not revenue)` +
        (sold.partial ? ' · only part of the month is measured' : '')
      : 'sold: not available for this period';
    out.pc_vs_fnb_month = {
      value: pcShare == null ? '—' : `${pcShare}% PC / ${100 - pcShare}% F&B`,
      sub: `${soldText} — paid in directly ${inr(paidIn.pc)} PC · ${inr(paidIn.fnb)} F&B · ` +
        `${inr(paidIn.topups)} of top-ups can't be split`,
    };
  }
  // Cash vs online is the split of MONEY IN (revenue) by how it was paid — it adds up to
  // the revenue figure, and a wallet-funded purchase is not in it (that money arrived at
  // top-up). The headline is cash's share; with no money in, there is no share to show, so
  // it reads "—" rather than a 0% that would claim online took everything.
  if (cashOnline && cashOnline.today != null) {
    const { today, this_week: week, this_month: month } = cashOnline;
    const total = today.cash + today.online + today.other;
    const share = total > 0 ? `${Math.round((100 * today.cash) / total)}% cash` : '—';
    const unattributed = today.other > 0 ? ` · ${inr(today.other)} with no method recorded` : '';
    out.cash_online_today = {
      value: share,
      sub: `today ${inr(today.cash)} cash / ${inr(today.online)} online · month ${inr(month?.cash)} / ${inr(month?.online)}` +
        (week ? ` · week ${inr(week.cash)} / ${inr(week.online)}` : '') + unattributed,
    };
  }
  // NoLag revenue = the platform fee earned (GST included, the figure Billing shows),
  // with what cafés still owe under it. "Owed" is a balance, all time — not per period.
  // A café that could not be read counts as 0 in every figure, and "owes nothing" looks
  // the same as "could not look", so that is said on the tile instead of left implicit.
  // null = not measured (single-DB dev): leave the tiles unfilled.
  if (nolag && nolag.today != null) {
    const unreadable = nolag.cafes_unreachable > 0
      ? ` · ⚠ ${nolag.cafes_unreachable} ${nolag.cafes_unreachable === 1 ? 'café' : 'cafés'} could not be read`
      : '';
    out.nolag_today = {
      value: inr(nolag.today.earned),
      sub: `week ${inr(nolag.this_week?.earned)} · month ${inr(nolag.this_month?.earned)} · ${inr(nolag.owed)} still owed by cafés${unreadable}`,
    };
    const since = nolag.oldest_unsettled_at
      ? istDay(nolag.oldest_unsettled_at)
      : null;
    out.collections_pending = {
      value: inr(nolag.owed),
      sub: (since ? `cash fees unpaid, oldest since ${since}` : 'no cash fees outstanding') + unreadable,
    };
    // Receivables = what cafés owe us (same balance); Collections = cash fees actually
    // received in the window; Payables = payout money we are holding for cafés.
    out.receivables = out.collections_pending;
    out.collections = {
      value: inr(nolag.today.collected),
      sub: `week ${inr(nolag.this_week?.collected)} · month ${inr(nolag.this_month?.collected)} received from cafés` + unreadable,
    };
    if (nolag.payable != null) {
      const heldSince = nolag.payable_oldest_at
        ? istDay(nolag.payable_oldest_at)
        : null;
      out.payables = {
        value: inr(nolag.payable),
        sub: (heldSince ? `held for cafés, oldest since ${heldSince}` : 'nothing held for cafés') + unreadable,
      };
    }
  }
  // Café performance, last 7 days. Hours, revenue and sessions all cover the same
  // rolling 7 days, so the ratios divide like with like. Utilization is measured
  // against 24 h a day because opening hours are not recorded — the subtitle says so.
  // A ratio with nothing to divide by reads "—", never 0.
  const screens = dash?.total_pcs ?? 0;
  const hours7 = hours?.hours_7d;
  if (hours && screens > 0) {
    const capacity = screens * 24 * 7;
    out.screen_utilization = {
      value: `${Math.round((1000 * hours7) / capacity) / 10}%`,
      sub: `${num(hours7)} h played of ${num(capacity)} screen-hours (${num(screens)} screens × 24 h × 7 days)`,
    };
  }
  if (weekly) {
    if (weekly.revenue_7d != null) {
      if (screens > 0) {
        out.revenue_per_screen_7d = {
          value: inr(weekly.revenue_7d / screens),
          sub: `${inr(weekly.revenue_7d)} over the last 7 days across ${num(screens)} screens`,
        };
      }
      if (hours) {
        out.revenue_per_hour = {
          value: hours7 > 0 ? inr(weekly.revenue_7d / hours7) : '—',
          sub: `${inr(weekly.revenue_7d)} over ${num(hours7)} gaming hours, last 7 days`,
        };
      }
    }
    if (weekly.hour_counts) {
      const label = (h) => {
        const to = (h + 1) % 24;
        const t = (x) => `${x % 12 === 0 ? 12 : x % 12}${x < 12 ? ' AM' : ' PM'}`;
        return `${t(h)}–${t(to)}`;
      };
      const ranked = weekly.hour_counts
        .map((n, h) => ({ n, h }))
        .filter((x) => x.n > 0)
        .sort((a, b) => b.n - a.n || a.h - b.h);
      out.peak_hours = ranked.length
        ? {
            value: label(ranked[0].h),
            sub: `${num(ranked[0].n)} of ${num(weekly.sessions_7d)} sessions started then` +
              (ranked.length > 1 ? ` · next ${ranked.slice(1, 3).map((x) => label(x.h)).join(', ')}` : '') +
              ' · last 7 days',
          }
        : { value: '—', sub: 'no sessions in the last 7 days' };
    }
    if (weekly.tickets_open != null) {
      out.support_tickets = {
        value: num(weekly.tickets_open),
        sub: `open or in progress · ${num(weekly.tickets_new_7d)} raised in the last 7 days`,
        href: '/support',
      };
    }
    if (weekly.cafes_unreachable > 0) {
      const warn = ` · ⚠ ${weekly.cafes_unreachable} ${weekly.cafes_unreachable === 1 ? 'café' : 'cafés'} could not be read`;
      for (const k of ['revenue_per_screen_7d', 'revenue_per_hour', 'peak_hours']) {
        if (out[k]) out[k].sub += warn;
      }
    }
  }
  // Product and technology. An owner is "active" when a session token was issued to
  // them in the last 7 days (sign-in or silent renewal). Login frequency is the average
  // number of DAYS they were active — renewals can't inflate a day count the way they
  // would a count of sign-ins, which nothing records on their own.
  if (usage) {
    out.owners_active = {
      value: num(usage.owners_active_7d),
      sub: `of ${num(usage.owners_total)} café owners, last 7 days`,
    };
    out.owner_login_frequency = {
      value: usage.avg_active_days_7d == null ? '—' : `${usage.avg_active_days_7d} of 7 days`,
      sub: usage.owners_active_7d
        ? `average days active, across ${num(usage.owners_active_7d)} active ${usage.owners_active_7d === 1 ? 'owner' : 'owners'}`
        : 'no café owner was active in the last 7 days',
    };
    if (usage.recharges_7d != null) {
      const warn = usage.cafes_unreachable > 0
        ? ` · ⚠ ${usage.cafes_unreachable} ${usage.cafes_unreachable === 1 ? 'café' : 'cafés'} could not be read`
        : '';
      out.recharge_booking_usage = {
        value: `${num(usage.recharges_7d)} / ${num(usage.bookings_7d)}`,
        sub: `recharges / bookings, last 7 days · ${num(usage.recharge_players_7d)} players recharged · ` +
          `${num(usage.cafes_recharging_7d)} cafés recharging, ${num(usage.cafes_booking_7d)} taking bookings` + warn,
      };
    }
  }
  // Uptime is the platform's own 30-day health log (one check every 5 minutes). It
  // stays unfilled until the log has samples — a percentage from none would be a guess.
  if (health && health.uptime_percent != null) {
    const mttr = health.mttr_hours != null ? ` · avg recovery ${health.mttr_hours} h` : '';
    out.platform_uptime = {
      value: `${health.uptime_percent}%`,
      sub: `last 30 days${mttr}${health.active_incidents ? ' · incident in progress' : ''}`,
      href: '/system-health',
    };
  }
  // null = not measured (single-DB dev): leave the tile unfilled instead of ₹0.
  if (recharge && recharge.today != null) {
    out.recharge_today = {
      value: inr(recharge.today),
      sub: `week ${inr(recharge.this_week)} · month ${inr(recharge.this_month)}`,
    };
  }
  return out;
}
